import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { BriefResponseDto } from '../../../../../../common/general-types/briefs.dto';
import { SubmittedPostDto } from '../../../../../../common/general-types/submitted-posts.dto';
import { FormattedCommentSentiment, LatestPostResultsDto } from '../../../../../../common/general-types/super-social.dto';
import { DecimalPipe } from '@angular/common';
import { ImageService } from '../../../../../../services/image/image.service';
import { FormatService } from '../../../../../../common/format/format.service';

@Component({
  selector: 'mbtr-sentiment',
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: './sentiment.component.html',
  styleUrl: './sentiment.component.css'
})
export class SentimentComponent implements OnInit {

  @Input() focussedBrief!: BriefResponseDto;
  @Input() submittedPosts!: SubmittedPostDto[];
  @Input() postsList: any;

  sentimentPercentages = signal<{ label: 'Positive' | 'Neutral' | 'Negative', percentage: number, icon: string, color: string }[]>([
    { label: 'Positive', percentage: 70, icon: 'thumbs-up', color: 'text-green-600' },
    { label: 'Neutral', percentage: 20, icon: 'expressionless', color: 'text-yellow-500' },
    { label: 'Negative', percentage: 10, icon: 'thumbs-down', color: 'text-red-600' },
  ]);

  superSocialResults = signal<LatestPostResultsDto[] | undefined>(undefined);

  /** Display Variables */
  weightedAverage = signal<number | undefined>(undefined);
  totalComments = signal<number | undefined>(undefined);
  allCommentAnalysis = signal<FormattedCommentSentiment[] | undefined>(undefined);

  positivityBuckets = signal<{
    positive: number,
    neutral: number,
    negative: number
  }>({
    positive: 0,
    neutral: 0,
    negative: 0
  });

  topPerformers = signal<{ handle: string, score: number, caption: string, likes: number, comments: number, shares: number, videoLink: string }[] | undefined>(undefined);
  /** Display Variables - END */

  /** Services */
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  /** Services - END */

  ngOnInit(): void {
    if (this.submittedPosts && this.submittedPosts.length > 0) {
      this.totalComments.set(0);
      this.allCommentAnalysis.set([]);

      Object.keys(this.postsList).forEach(postId => {
        const post = this.postsList[postId];
        this.superSocialResults.set([...this.superSocialResults() ?? [], post]);
        if (post.commentSentiment) {
          this.totalComments.set(this.totalComments()! + post.commentSentiment.comments.length);
          this.allCommentAnalysis.set([...this.allCommentAnalysis()!, post.commentSentiment]);
        }
      });

      this.superSocialResults()!.sort((a, b) => (b.commentSentiment?.positivity_rating_with_likes || 0) - (a.commentSentiment?.positivity_rating_with_likes || 0));

      this.topPerformers.set(this.superSocialResults()!.slice(0, 5).filter((result: LatestPostResultsDto) => result.commentSentiment?.comments.length && result.commentSentiment?.comments.length > 0).map((result: LatestPostResultsDto) => ({
        handle: result.handle.replace('@', ''),
        score: result.commentSentiment?.positivity_rating_with_likes || 0,
        caption: result.commentSentiment?.overall_message || '',
        likes: result.likes,
        comments: result.commentSentiment?.comments.length || 0,
        shares: result.shares,
        videoLink: result.submittedPost?.videoLink
      })));

      this.aggregateCommentAnalysis();
    }
  }

  aggregateCommentAnalysis() {
    if (this.allCommentAnalysis()) {
      this.weightedAverage.set(this.getWeightedAveragePositivityRating());
    }
  }

  getWeightedAveragePositivityRating(): number {
    if (!this.allCommentAnalysis() || this.allCommentAnalysis()!.length === 0) {
      return 0;
    }

    let totalWeightedScore = 0;
    let totalComments = 0;

    this.allCommentAnalysis()!.forEach((analysis: FormattedCommentSentiment) => {
      const commentCount = analysis.comments.length;
      const positivityRating = analysis.positivity_rating_with_likes;

      totalWeightedScore += positivityRating * commentCount;
      totalComments += commentCount;

      if (positivityRating > 7) {
        this.positivityBuckets.set({ ...this.positivityBuckets(), positive: this.positivityBuckets().positive + commentCount });
      } else if (positivityRating > 4.5) {
        this.positivityBuckets.set({ ...this.positivityBuckets(), neutral: this.positivityBuckets().neutral + commentCount });
      } else {
        this.positivityBuckets.set({ ...this.positivityBuckets(), negative: this.positivityBuckets().negative + commentCount });
      }
    });

    // Return weighted average, or 0 if no comments
    return totalComments > 0 ? totalWeightedScore / totalComments : 0;
  }

  getSentimentPercentage(sentiment: 'Positive' | 'Neutral' | 'Negative') {
    if (sentiment === 'Positive') {
      return this.positivityBuckets().positive;
    } else if (sentiment === 'Neutral') {
      return this.positivityBuckets().neutral;
    } else {
      return this.positivityBuckets().negative;
    }
  }

}
